import './DocumentPanel.css';
import React, { useState } from 'react';
import type { Document, Task, DocumentType } from '../types';
import { FileText, Plus, Search, MoreVertical, ExternalLink, FolderOpen, Github } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Drive link configuration - ACTUAL FOLDER IDs
const DRIVE_LINKS = {
  root: 'https://drive.google.com/drive/folders/1M09KSPpIXbUsA45oFZ7WMOXCyIAJBnLx',
  projects: 'https://drive.google.com/drive/folders/1kiPx6e8YL5gpx3x96BoiRkZ9F4x_BVKa',
  workspace: 'https://drive.google.com/drive/folders/1qak5drl5Ccu3NzltWjisLew1kUmWwJO7',
  standups: 'https://drive.google.com/drive/folders/1dVivAk8hLkHYMIK_7XYaccMrfYbd7OmF',
  daily: 'https://drive.google.com/drive/folders/1RA-r4bmkIXdQ2iRY9rCZDSQVyQ1UgEZd',
  n8n: 'https://drive.google.com/drive/folders/1nCy-yiMnlSQMfadRBgGE0nFjf8ydQWvt',
};

const GITHUB_LINK = 'https://github.com/Arise-Arise/mission-control/tree/main';

interface DocumentPanelProps {
  documents: Document[];
  tasks: Task[];
  onDocumentsChange?: (documents: Document[]) => void;
  onCreateDocument?: (doc: { title: string; content: string; type: DocumentType }) => Promise<void> | void;
}

const documentTypeLabels: Record<DocumentType, string> = {
  deliverable: 'Deliverable',
  research: 'Research',
  protocol: 'Protocol',
  note: 'Note',
};

const DocumentPanel: React.FC<DocumentPanelProps> = ({ documents, tasks, onDocumentsChange, onCreateDocument }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<DocumentType | 'all'>('all');
  const [viewingDoc, setViewingDoc] = useState<Document | null>(null);
  const [showNewDocForm, setShowNewDocForm] = useState(false);
  const [newDocTitle, setNewDocTitle] = useState('');
  const [newDocContent, setNewDocContent] = useState('');
  const [newDocType, setNewDocType] = useState<DocumentType>('note');

  // Filter docs that have drive links or are synced from WORKING.md
  const filteredDocs = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (doc.content && doc.content.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = selectedType === 'all' || doc.type === selectedType;
    return matchesSearch && matchesType;
  });

  const getLinkedTask = (taskId?: string) => tasks.find(t => t.id === taskId);

  const handleCreateDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDocTitle.trim()) return;

    const content = newDocContent || '# New Document\n\nStart writing...';

    if (onCreateDocument) {
      await onCreateDocument({
        title: newDocTitle,
        content,
        type: newDocType,
      });
    } else {
      const newDoc: Document = {
        id: `doc-${Date.now()}`,
        title: newDocTitle,
        content,
        type: newDocType,
        authorId: 'arise',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const updatedDocs = [newDoc, ...documents];
      onDocumentsChange?.(updatedDocs);
    }

    setNewDocTitle('');
    setNewDocContent('');
    setNewDocType('note');
    setShowNewDocForm(false);
  };

  const openDriveLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="document-panel">
      {/* Pinned GitHub Link */}
      <div className="github-banner">
        <a 
          href={GITHUB_LINK} 
          target="_blank" 
          rel="noopener noreferrer"
          className="github-link"
        >
          <Github size={20} />
          <span>View all documents on GitHub</span>
          <ExternalLink size={14} />
        </a>
      </div>

      {/* Drive Links Section */}
      <div className="drive-links-section">
        <h3>📁 Google Drive Links</h3>
        <div className="drive-links-grid">
          <button className="drive-link-card" onClick={() => openDriveLink(DRIVE_LINKS.root)}>
            <FolderOpen size={24} />
            <span>All Documents</span>
            <ExternalLink size={14} />
          </button>
          <button className="drive-link-card" onClick={() => openDriveLink(DRIVE_LINKS.workspace)}>
            <FileText size={24} />
            <span>Core Docs</span>
            <ExternalLink size={14} />
          </button>
          <button className="drive-link-card" onClick={() => openDriveLink(DRIVE_LINKS.daily)}>
            <FileText size={24} />
            <span>Daily Logs</span>
            <ExternalLink size={14} />
          </button>
          <button className="drive-link-card" onClick={() => openDriveLink(DRIVE_LINKS.projects)}>
            <FileText size={24} />
            <span>Projects</span>
            <ExternalLink size={14} />
          </button>
          <button className="drive-link-card" onClick={() => openDriveLink(DRIVE_LINKS.standups)}>
            <FileText size={24} />
            <span>Standups</span>
            <ExternalLink size={14} />
          </button>
          <button className="drive-link-card" onClick={() => openDriveLink(DRIVE_LINKS.n8n)}>
            <FileText size={24} />
            <span>n8n Workflows</span>
            <ExternalLink size={14} />
          </button>
        </div>
      </div>

      <div className="panel-header">
        <h2>MS Documents (Legacy)</h2>
        <button className="btn-primary" onClick={() => setShowNewDocForm(true)}>
          <Plus size={16} />
          New Document
        </button>
      </div>

      <div className="panel-toolbar">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-tabs">
          <button 
            className={selectedType === 'all' ? 'active' : ''}
            onClick={() => setSelectedType('all')}
          >
            All
          </button>
          <button 
            className={selectedType === 'deliverable' ? 'active' : ''}
            onClick={() => setSelectedType('deliverable')}
          >
            Deliverables
          </button>
          <button 
            className={selectedType === 'research' ? 'active' : ''}
            onClick={() => setSelectedType('research')}
          >
            Research
          </button>
          <button 
            className={selectedType === 'protocol' ? 'active' : ''}
            onClick={() => setSelectedType('protocol')}
          >
            Protocols
          </button>
        </div>
      </div>

      <div className="documents-notice">
        <p>⚠️ <strong>New:</strong> All documents are now stored in Google Drive. Use the links above for the latest versions.</p>
      </div>

      <div className="documents-grid">
        {filteredDocs.length === 0 && (
          <div className="empty-state">
            <p>No documents in MS.</p>
            <p>All documents migrated to Google Drive.</p>
          </div>
        )}
        {filteredDocs.map(doc => {
          const linkedTask = getLinkedTask(doc.taskId);
          
          return (
            <div 
              key={doc.id} 
              className="document-card legacy-doc"
              onClick={() => setViewingDoc(doc)}
            >
              <div className="doc-icon">
                <FileText size={24} />
              </div>
              
              <div className="doc-content">
                <h4>{doc.title}</h4>
                <span className={`doc-type ${doc.type}`}>
                  {documentTypeLabels[doc.type]}
                </span>
                <span className="legacy-badge">Legacy</span>
                
                {linkedTask && (
                  <div className="doc-task-link">
                    <ExternalLink size={10} />
                    <span>{linkedTask.title}</span>
                  </div>
                )}
                
                <p className="doc-preview">
                  {doc.content ? doc.content.slice(0, 150) : 'No content - stored in Drive'}...
                </p>
              </div>
              
              <div className="doc-footer">
                <span className="doc-date">
                  {formatDistanceToNow(new Date(doc.updatedAt), { addSuffix: true })}
                </span>
                <button className="doc-menu">
                  <MoreVertical size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Document Viewer Modal */}
      {viewingDoc && (
        <div className="modal-overlay" onClick={() => setViewingDoc(null)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="doc-viewer-header">
              <div>
                <h2>{viewingDoc.title}</h2>
                <span className={`doc-type ${viewingDoc.type}`}>
                  {documentTypeLabels[viewingDoc.type]}
                </span>
                <span className="legacy-badge">Legacy - View in Drive for latest</span>
              </div>
              <button className="btn-icon" onClick={() => setViewingDoc(null)}>
                ✕
              </button>
            </div>
            
            <div className="doc-viewer-content">
              <div className="doc-meta-bar">
                <span>Created: {new Date(viewingDoc.createdAt).toLocaleDateString()}</span>
                <span>Updated: {new Date(viewingDoc.updatedAt).toLocaleDateString()}</span>
              </div>
              
              <div className="doc-body">
                {viewingDoc.content || 'Content stored in Google Drive. Use Drive links above for full document.'}
              </div>
              
              <div className="doc-actions">
                <button 
                  className="btn-primary"
                  onClick={() => openDriveLink(DRIVE_LINKS.root)}
                >
                  <ExternalLink size={16} />
                  Open in Google Drive
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Document Modal */}
      {showNewDocForm && (
        <div className="modal-overlay" onClick={() => setShowNewDocForm(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="doc-viewer-header">
              <h2>Create New Document</h2>
              <button className="btn-icon" onClick={() => setShowNewDocForm(false)}>
                ✕
              </button>
            </div>
            
            <div className="doc-notice">
              <p>💡 <strong>Tip:</strong> For important documents, create them in Google Drive and link them here.</p>
            </div>
            
            <form onSubmit={handleCreateDocument} className="doc-viewer-content">
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  placeholder="Document title..."
                  value={newDocTitle}
                  onChange={e => setNewDocTitle(e.target.value)}
                  autoFocus
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Type</label>
                <select 
                  value={newDocType} 
                  onChange={e => setNewDocType(e.target.value as DocumentType)}
                  className="form-select"
                >
                  <option value="note">Note</option>
                  <option value="deliverable">Deliverable</option>
                  <option value="research">Research</option>
                  <option value="protocol">Protocol</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Content</label>
                <textarea
                  placeholder="Start writing..."
                  value={newDocContent}
                  onChange={e => setNewDocContent(e.target.value)}
                  rows={10}
                  className="form-textarea"
                />
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowNewDocForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Document
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentPanel;
